import { RoomStatus } from '../types.js'
import {
    BookingModel,
    BookingSchema,
} from './BookingModel.js'
import { z } from 'zod'

export const RoomSchema = z.object({
    id: z.string().describe('The unique identifier of the room'),
    name: z.string().describe('The name of the room'),
    capacity: z.number().int().min(1).optional().describe('The maximum number of people the room can accommodate'),
    email: z.string().email().describe('The email address associated with the room'),
    events: z.array(BookingSchema).describe('List of events in the room'),
})

export type Room = z.infer<typeof RoomSchema>

export const notBookableRoomIds: string[] = [
    '98363918103',
]

export class RoomModel implements Room {

    public id: string
    public roomName: string
    public email: string
    public events: BookingModel[]
    public capacity?: number

    constructor(room: Room) {
        this.id = room.id
        this.roomName = room.name
        this.email = room.email
        this.capacity = room.capacity
        this.events = room.events.map(event => new BookingModel(event, this))
    }

    get isBookable(): boolean {
        return !notBookableRoomIds.includes(this.id)
    }

    get name() {
        if (this.capacity) {
            return `${this.roomName} (${this.capacity})`
        }

        return this.roomName
    }

    get status(): RoomStatus {
        if (this.currentBooking) {
            return RoomStatus.OCCUPIED
        } else if (this.nextBooking) {
            return RoomStatus.UPCOMING
        }

        return RoomStatus.AVAILABLE
    }

    public isAvailable(startDateTime: Date, endDateTime: Date): boolean {
        if (!this.isBookable) {
            return false
        }

        if (this.events.length === 0) {
            return true
        }

        return !this.events.some(booking => {
            return (
                (booking.startTime <= startDateTime && booking.endTime > startDateTime) || // Event is ongoing at our start time
                (booking.startTime >= startDateTime && booking.startTime < endDateTime) || // Event starts during our booking
                (booking.startTime <= startDateTime && booking.endTime >= endDateTime) // Event encompasses our entire booking
            )
        })
    }

    get currentBooking(): BookingModel | undefined {
        const now = new Date()
        // Check for current booking
        for (const event of this.events) {
            if (event.startTime <= now && event.endTime > now) {
                return event
            }
        }

        return undefined
    }

    get nextBooking(): BookingModel | undefined {
        const now = new Date()
        for (const event of this.events) {
            if (event.endTime <= now) {
                continue
            }

            if (event.startTime > now) {
                return event
            }
        }

        return undefined
    }

    get timeUntilNextMeeting(): number | undefined {
        if (!this.nextBooking) {
            return undefined
        }

        const now = new Date()

        return Math.round((this.nextBooking.startTime.getTime() - now.getTime()) / (60 * 1000))
    }
}
